import { useState, useEffect } from 'preact/hooks';
import { api } from '../../api/client';
import { scStyles } from './styles';
import type { SkillMeta, Machine } from './types';
import { SkeletonBar } from '../ui';
import { T } from '../../styles/tokens';
import { Button } from '../ui';

interface Props {
  search: string;
}

export function TabInstalled({ search }: Props) {
  const [machines, setMachines] = useState<Machine[]>([]);
  const [selectedMachine, setSelectedMachine] = useState('');
  const [skills, setSkills] = useState<SkillMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [toast, setToast] = useState('');

  useEffect(() => {
    api.get<any>('/runner-v3/list')
      .then((r: any) => {
        const list = r?.data?.runners || r?.runners || [];
        setMachines(list);
        if (list.length > 0) {
          setSelectedMachine(list[0].id);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedMachine) return;
    setLoadingSkills(true);
    api.get<any>(`/runner-v3/${selectedMachine}/skills`)
      .then((r: any) => {
        const list = r?.skills || r?.data?.skills || r?.data || [];
        setSkills(Array.isArray(list) ? list : []);
      })
      .catch(() => setSkills([]))
      .finally(() => setLoadingSkills(false));
  }, [selectedMachine]);

  const filtered = skills.filter(s => {
    if (!search) return true;
    return s.name.toLowerCase().includes(search.toLowerCase());
  });

  const handleUninstall = async (name: string) => {
    setActionLoading(name);
    try {
      await api.post(`/runner-v3/${selectedMachine}/skills/uninstall`, { skillName: name });
      setSkills(prev => prev.filter(s => s.name !== name));
      showToast(`${name} desinstalada`);
    } catch { showToast('Erro ao desinstalar'); }
    setActionLoading('');
  };

  const handleUpdate = async (name: string) => {
    setActionLoading(name);
    try {
      await api.post(`/runner-v3/${selectedMachine}/skills/update`, { skillName: name });
      showToast(`${name} atualizada`);
    } catch { showToast('Erro ao atualizar'); }
    setActionLoading('');
  };

  const handleInstallAllVerified = async () => {
    setActionLoading('all');
    try {
      await api.post(`/runner-v3/${selectedMachine}/skills/install-all-verified`, {});
      showToast('Todas verificadas instaladas!');
      // Refresh
      const r = await api.get<any>(`/runner-v3/${selectedMachine}/skills`);
      const list = (r as any)?.skills || (r as any)?.data?.skills || (r as any)?.data || [];
      setSkills(Array.isArray(list) ? list : []);
    } catch { showToast('Erro ao instalar'); }
    setActionLoading('');
  };

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  if (loading) return (<div style={{ padding: 16 }}>{[1,2,3].map(i => (<div key={i} style={{ background: T.bgCard, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 16, marginBottom: 8 }}><SkeletonBar width="60%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="40%" height={12} /></div>))}</div>);

  return (
    <div>
      {/* Machine selector */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          value={selectedMachine}
          onChange={(e: any) => setSelectedMachine(e.target.value)}
          style={{
            padding: '10px 14px', borderRadius: '10px',
            background: T.purpleBg, border: `1px solid ${T.purpleBgStrong}`,
            color: T.text, fontSize: '14px', outline: 'none', minWidth: '200px',
          }}
        >
          {machines.length === 0 && <option value="" style={{ background: T.bg }}>Nenhuma máquina</option>}
          {machines.map(m => (
            <option key={m.id} value={m.id} style={{ background: T.bg }}>{m.name || m.id}</option>
          ))}
        </select>
        <Button variant="ghost"
          onClick={handleInstallAllVerified}
          disabled={actionLoading === 'all' || !selectedMachine}
          style={{
            padding: '10px 20px', borderRadius: '10px', border: 'none',
            background: 'linear-gradient(135deg, #10B981, #059669)', color: T.text,
            fontSize: '13px', fontWeight: 600, cursor: 'pointer',
            opacity: actionLoading === 'all' ? 0.5 : 1,
          }}
        >
 {actionLoading === 'all' ? 'Instalando...' : 'Instalar Todas Verificadas'}
        </Button>
        <span style={{ fontSize: '13px', color: T.textSecondary }}>{filtered.length} skills instaladas</span>
      </div>

      {/* Skills list */}
      {loadingSkills ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} style={{ height: '60px', borderRadius: '12px', background: `linear-gradient(90deg, ${T.purpleBgSubtle} 25%, ${T.purpleBg} 50%, ${T.purpleBgSubtle} 75%)`, backgroundSize: '200% 100%', animation: 'skeletonShimmer 1.5s infinite' }} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: T.textSecondary }}>
 <div style={{ fontSize: '48px', marginBottom: '12px' }}></div>
          <div style={{ fontSize: '16px', fontWeight: 600 }}>Nenhuma skill instalada</div>
          <div style={{ fontSize: '13px', marginTop: '8px' }}>Use "Instalar Todas Verificadas" para começar</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {filtered.map((s, i) => (
            <div key={s.name} style={{
              ...scStyles.glass, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '12px',
              animation: `fadeInUp 0.3s ease ${i * 0.03}s both`,
            }}>
 <span style={{ fontSize: '24px' }}>{s.emoji || ''}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: T.text }}>{s.name}</div>
                <div style={{ fontSize: '11px', color: T.textSecondary }}>{s.version || 'v1.0'} • {s.category || 'Geral'}</div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button variant="ghost" onClick={() => handleUpdate(s.name)} disabled={actionLoading === s.name}
                  style={{ padding: '6px 12px', borderRadius: '8px', border: `1px solid ${T.primaryBgStrong}`, background: T.primaryBg, color: T.primary, fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>
                  {actionLoading === s.name ? '...' : '↑ Atualizar'}
                </Button>
                <Button variant="ghost" onClick={() => handleUninstall(s.name)} disabled={actionLoading === s.name}
                  style={{ padding: '6px 12px', borderRadius: '8px', border: `1px solid ${T.dangerBgStrong}`, background: T.dangerBg, color: T.danger, fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>
                  Desinstalar
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '24px', right: '24px', padding: '12px 20px',
          borderRadius: '10px', background: T.bgOverlay, border: `1px solid ${T.purpleBgStrong}`,
          color: T.text, fontSize: '13px', fontWeight: 600, zIndex: 3000, animation: 'fadeInUp 0.3s ease',
        }}>
          {toast}
        </div>
      )}
    </div>
  );
}
